import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { bindable } from 'aurelia-typed-observable-plugin';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { CaseService } from 'app/cm/services/case-service';
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions'
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class MemberCaseManagement extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    protected cases: ReadonlyArray<CM.Case> = [];

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected hasCaseRoute = false;
    protected openCaseSummary = openCaseSummary;

    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private readonly caseService: CaseService,
        private readonly customFieldContext: CustomFieldContext,
        ...rest: any[]) {
        super(...rest);
        this.checkPermissions();

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'metadata.statusChanged.timestamp',
                direction: SortDirection.Descending
            }
        ];
    }

    protected bind(): void {
        this.loadCustomFieldTargets();
        this.loadCases();
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        await this.loadCases();
    }

    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(sorting.entries.length === 0) {
                this.sorting.entries = [
                    {
                        reference: 'metadata.statusChanged.timestamp',
                        direction: SortDirection.Descending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ************************************************////

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
    }

    ////****************** Service Calls ********************************************////
    private async loadCustomFieldTargets(): Promise<void> {
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CM.Case', { definitionCount: 10 });
    }

    @apiCall
    private async loadCases(): Promise<void> {
        if(!this.memberId) {
            return;
        }

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };

        const includes = [
            'acuity',
            'assignedTo',
            'custom',
            'intensity',
            'metadata.created.byuser',
            'program',
            'program.group',
            'referral',
            'referral.destination',
            'referral.reason',
            'referral.source',
            'referral.types',
            'status',
            'status.reason'
        ];

        const fields = [
            'acuity.description',
            'assignedTo.name.*',
            'custom.lookup.*',
            'custom.text.*',
            'intensity.description',
            'metadata.created.byUser.description',
            'metadata.created.timestamp',
            'metadata.statusChanged.timestamp',
            'number',
            'program.description',
            'program.group.description',
            'referral.destination.description',
            'referral.reason.description',
            'referral.source.description',
            'referral.types.description',
            'status.description',
            'status.reason.description'
        ];

        try {
            this.state.beginLoad();

            const response = await this.caseService.loadCasesByMember(this.memberId, options, includes, fields);
            this.cases = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
